import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, shareReplay, catchError } from 'rxjs/operators';
import { TipoEtapaService } from './tipo-etapa.service';
import { TipoEtapa } from '@/models';

@Injectable({
  providedIn: 'root'
})
export class EtapaFormatService {
  private tiposEtapaCache$: Observable<Map<string, string>> | null = null;
  private tiposEtapaMap: Map<string, string> = new Map(); // Cache síncrono do mapa

  constructor(private tipoEtapaService: TipoEtapaService) {
    // Inicializar o cache ao criar o serviço
    this.initializeCache();
  }

  /**
   * Inicializa o cache de tipos de etapa
   */
  private initializeCache(): void {
    this.getTiposEtapaMap().subscribe({
      next: (mapa) => {
        this.tiposEtapaMap = mapa;
      }
    });
  }

  /**
   * Busca os tipos de etapa do backend e cria um mapa código -> nome
   * Usa cache para evitar múltiplas requisições
   */
  getTiposEtapaMap(): Observable<Map<string, string>> {
    if (!this.tiposEtapaCache$) {
      this.tiposEtapaCache$ = this.tipoEtapaService.getAll(true).pipe(
        map((tipos: TipoEtapa[]) => {
          const mapa = new Map<string, string>();
          tipos.forEach(tipo => {
            // Usa o nome do tipo de etapa como descrição formatada
            mapa.set(tipo.codigo, tipo.nome);
          });
          // Atualiza o cache síncrono
          this.tiposEtapaMap = mapa;
          return mapa;
        }),
        shareReplay(1), // Cache o resultado
        catchError(error => {
          console.error('Erro ao buscar tipos de etapa:', error);
          // Retorna mapa vazio em caso de erro
          return of(new Map<string, string>());
        })
      );
    }
    return this.tiposEtapaCache$;
  }

  /**
   * Formata o código da etapa para sua descrição legível
   * @param codigoEtapa Código da etapa (ex: "INSCRICAO", "RECURSO")
   * @returns Observable com a descrição formatada
   */
  formatEtapa(codigoEtapa: string | null | undefined): Observable<string> {
    if (!codigoEtapa) {
      return of('Sem etapa');
    }

    // Tratamento especial para etapas que começam com PROVA_
    if (codigoEtapa.startsWith('PROVA_')) {
      return of('Realização prova');
    }

    return this.getTiposEtapaMap().pipe(
      map(mapa => {
        // Busca no mapa do backend
        const descricao = mapa.get(codigoEtapa);
        if (descricao) {
          return descricao;
        }
        // Se não encontrar, retorna o código original
        return codigoEtapa;
      })
    );
  }

  /**
   * Formata o código da etapa de forma síncrona (usa cache se disponível)
   * Retorna o código original se o cache ainda não estiver carregado
   * @param codigoEtapa Código da etapa
   * @returns Descrição formatada ou código original se não encontrado
   */
  formatEtapaSync(codigoEtapa: string | null | undefined): string {
    if (!codigoEtapa) {
      return 'Sem etapa';
    }

    // Tratamento especial para etapas que começam com PROVA_
    if (codigoEtapa.startsWith('PROVA_')) {
      return 'Realização prova';
    }

    // Busca no cache síncrono
    if (this.tiposEtapaMap.has(codigoEtapa)) {
      return this.tiposEtapaMap.get(codigoEtapa)!;
    }

    // Se não encontrar, retorna o código original
    return codigoEtapa;
  }

  /**
   * Limpa o cache (útil para forçar atualização)
   */
  clearCache(): void {
    this.tiposEtapaCache$ = null;
  }
}
